// TaskDetails.tsx
import { Box, CircularProgress, Divider, Grid, Typography, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import ConfigForm from 'components/Tasks/ConfigForm'
import { buildInitialValues, getTypeConfigs, splitPatchesForSave } from 'components/Tasks/field-utils'
import React, { useState } from 'react'
import { Attachments, BatchUpdateItem, OrganizationTableItem, Task } from '../../types'
import CreativeTaskDetails from './CreativeTaskDetails'
import DigitalTaskDetails from './DigitalTaskDetails'
import NavigateToOrg from './NavigateToOrg'
import './style.css'
type Item = { id: string; label: string; done?: boolean }

interface Subtask {
  PK: string
  SK: string
  parentSK: string
}
interface TaskDetailsProps {
  task: Task
  isTaskLoading: boolean
  organization: OrganizationTableItem
  onChange: (field: string, newValue: any) => void
  addtlNotes: string
  expanded: boolean
  editing?: boolean
  onNavigate: (route: string) => void
  registerTypeUpdateBuilder?: (fn: () => BatchUpdateItem[]) => void
}

const TaskDetails: React.FC<TaskDetailsProps> = ({
  task,
  isTaskLoading,
  organization,
  onChange,
  addtlNotes,
  expanded,
  editing,
  onNavigate,
  registerTypeUpdateBuilder,
}) => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const [currentAttachments, setCurrentAttachments] = useState<Attachments[]>([])
  const { top: topCfg, extra: extraCfg } = React.useMemo(() => getTypeConfigs(task), [task?.type])
  const mergedCfg = React.useMemo(() => ({ ...topCfg, ...extraCfg }), [topCfg, extraCfg])
  // local draft for *type* fields (separate from addtlNotes / checklist)
  const initialTypeVals = React.useMemo(() => buildInitialValues(task, topCfg, extraCfg), [task, topCfg, extraCfg])
  const [typeDraft, setTypeDraft] = React.useState<Record<string, any>>(initialTypeVals)

  // keep in sync when task changes
  React.useEffect(() => setTypeDraft(initialTypeVals), [initialTypeVals])

  const onTypeChange = (key: string, val: any) => setTypeDraft(prev => ({ ...prev, [key]: val }))

  // 🔽 the batch builder (diffs current draft vs task)
  const buildTypeUpdates = React.useCallback((): BatchUpdateItem[] => {
    const { topPatch, extraPatch } = splitPatchesForSave(task, typeDraft, topCfg, extraCfg)
    const updates: BatchUpdateItem[] = []

    for (const [fieldName, newValue] of Object.entries(topPatch)) {
      updates.push({
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        itemId: task.PK,
        itemSortKey: task.SK, // must start with "TASK_" in your API
        fieldName,
        newValue,
      })
    }

    if (Object.keys(extraPatch).length) {
      updates.push({
        primaryKey: 'PK',
        sortKey: 'SK',
        tableName: 'TASKS_TABLE',
        itemId: task.PK,
        itemSortKey: task.SK,
        fieldName: 'extraFields',
        newValue: { ...(task.extraFields ?? {}), ...extraPatch }, // merge client-side
      })
    }
    return updates
  }, [task, typeDraft, topCfg, extraCfg])

  React.useEffect(() => {
    registerTypeUpdateBuilder?.(() => buildTypeUpdates())
  }, [registerTypeUpdateBuilder, buildTypeUpdates])
  const isNotesChanged = task?.addtlNotes !== addtlNotes
  return isTaskLoading ? (
    <CircularProgress />
  ) : (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Box sx={{ display: 'inline-flex', width: '100%', alignItems: 'center', p: 1 }}>
            <Typography sx={{ color: 'text.secondary' }}>Organization</Typography>
            <NavigateToOrg task={task} organization={organization} handleNavigate={onNavigate} />
          </Box>
          <Divider flexItem orientation="horizontal" />
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          height: '100vh',
          width: '100%',
          maxHeight: expanded ? 'calc(100vh - 285px)' : 'calc(100vh - 245px)',
          bgcolor: theme.palette.background.section,
          transition: 'all 0.3s ease',
          overflow: 'auto', // was 'hidden' – allows content to fill width and scroll
          // p: 1, // give the items breathing room
        }}
        // spacing={1}
      >
        {/* {organization && (
        <Grid item xs={12}>
          <Box sx={{ display: 'inline-flex', width: '100%', alignItems: 'center', p: 1 }}>
            <Typography sx={{ color: 'text.secondary' }}>Organization</Typography>
            <NavigateToOrg task={task} organization={organization} handleNavigate={onNavigate} />
          </Box>
          <Divider flexItem orientation="horizontal" />
        </Grid>
      )} */}

        {/* Marketing details */}
        {task?.isMarketing && !['socialAds', 'displayAds'].includes(task?.type) && (
          <Grid item xs={12}>
            <DigitalTaskDetails task={task} />
          </Grid>
        )}

        {/* Creative details */}
        {['socialAds', 'displayAds'].includes(task?.type) && (
          <Grid item xs={12}>
            <CreativeTaskDetails task={task} />
          </Grid>
        )}

        {/* Creative / Website edit modes */}
        {task?.isCreative && editing && (
          <Grid item xs={12}>
            <ConfigForm config={mergedCfg} values={typeDraft} onChange={onTypeChange} disabled={false} columns={2} />
          </Grid>
        )}

        {task?.isCreative && !editing && (
          <Grid item xs={12}>
            <CreativeTaskDetails task={{ ...task, extraFields: { ...(task.extraFields ?? {}) } }} />
          </Grid>
        )}

        {(task?.type === 'website' || task?.isWebsite) && editing && (
          <Grid item xs={12}>
            <ConfigForm config={mergedCfg} values={typeDraft} onChange={onTypeChange} disabled={false} columns={2} />
          </Grid>
        )}

        {(task?.type === 'website' || task?.isWebsite) && !editing && (
          <Grid item xs={12}>
            <CreativeTaskDetails task={task} />
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default TaskDetails
